import { observer } from "mobx-react-lite";
import { FC } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { DialogHeader } from "@/components/ui/dialog";
import { DialogDescription, DialogTitle } from "@radix-ui/react-dialog";
import { FormInput } from "@/components/forms/FormInput";
import { FormSubmitButton } from "@/components/forms/FormSubmitButton";
import { FormWrapper } from "@/components/forms/FormWrapper";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useFormHandler } from "@/hooks/useFormHandler";
import { useStore } from "@/hooks/useStore";
import { addStructureProperties, updateStructureProperties } from "@/endpoints/dryp/addStructureProperties";
import { StructurePropertyListItem } from "@/endpoints/dryp/structureProperty";

const schema = z.object({
	value: z.coerce.number({ invalid_type_error: "Enter a number" }),
});

type AddOrUpdateFormValues = z.infer<typeof schema>;

interface AddOrUpdateStructurePropertyProps {
	structureId: Dryp.StructureId;
	property: StructurePropertyListItem;
	onSuccess: () => void;
}

// "PipeDiameterInMillimeters" → "Pipe Diameter In Millimeters"
const formatPropertyName = (name: string): string =>
	name
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
		.trim();

export const AddOrUpdateStructureProperty: FC<AddOrUpdateStructurePropertyProps> = observer(
	({ structureId, property, onSuccess }) => {
		const {
			uiStore: { modalStore },
		} = useStore();

		const isEditing = property.value !== null;
		const saveApi = useAsyncStateStore(isEditing ? updateStructureProperties : addStructureProperties);
		const propertyName = formatPropertyName(property.typeName);
		const valueLabel = property.unit ? `Value (${property.unit})` : "Value";

		const { form, handleFormErrors } = useFormHandler<AddOrUpdateFormValues>(schema, {
			defaultValues: { value: property.value ?? undefined },
		});

		const onSubmit = async (data: AddOrUpdateFormValues) => {
			try {
				await saveApi.fetch(structureId, [{ typeId: property.typeId, value: data.value }]);
				toast.success(isEditing ? `${propertyName} updated` : `${propertyName} added`);
				modalStore.close();
				onSuccess();
			} catch (error) {
				handleFormErrors(error);
			}
		};

		return (
			<>
				<DialogHeader>
					<DialogTitle>
						{isEditing ? "Edit" : "Add"} {propertyName}
					</DialogTitle>
					<DialogDescription className="sr-only">
						{isEditing ? "Update" : "Add"} the value of {propertyName} for this structure.
					</DialogDescription>
				</DialogHeader>

				<FormWrapper form={form} onSubmit={onSubmit} error={saveApi.error}>
					<FormInput name="value" type="number" label={valueLabel} placeholder="Enter a value" required />
					<FormSubmitButton
						isLoading={saveApi.loading}
						buttonText={isEditing ? "Save value" : "Add value"}
						buttonLoadingText="Saving"
					/>
				</FormWrapper>
			</>
		);
	},
);
